import React, { useState, useCallback, useMemo } from 'react'

import { Stack, Spinner, useToast } from '@pnld/components-web'

import {
  useDashboardPredictionYears,
  useDashboardCensusYears,
  useDashboardComparative,
} from '@/api/dashboard/queries'
import {
  DashboardComparativePayload,
  DashboardComparativeResponse,
} from '@/api/dashboard/types'
import { getErrorMessage } from '@/utils/api'

import { GeoMapFilter, GeoMapFilterValues } from '../parts'
import { ComparativeSingleYear } from './parts'
import { ComparativeType } from './types'

const Comparative: React.FC = () => {
  const toast = useToast()
  const [filterValues, setFilterValues] = useState<GeoMapFilterValues | null>(
    null
  )

  const errorHandler = useCallback(
    (error: unknown) => {
      toast({ status: 'error', title: getErrorMessage(error) })
    },
    [toast]
  )

  const mapGeoMapFilterToComparativePayload = useCallback(
    (values: GeoMapFilterValues): DashboardComparativePayload => {
      const predictionYearStr = Array.isArray(values.predictionYears)
        ? values.predictionYears[0]
        : values.predictionYears || ''
      const censusYearStr = Array.isArray(values.censusYears)
        ? values.censusYears[0]
        : values.censusYears || ''

      const [predictionYear, predictionBaseCensusYear] =
        predictionYearStr.split('#')

      return {
        nuPredictionYear: Number(predictionYear),
        nuPredictionBaseCensusYear: Number(predictionBaseCensusYear),
        nuCensusYear: Number(censusYearStr),
        coRegion: values.region || undefined,
        coUf: values.states.length ? values.states : undefined,
        coCity: values.cities.length ? values.cities : undefined,
        coEntity: values.schools.length ? values.schools : undefined,
        tpDependency: values.dependencies.length
          ? values.dependencies
          : undefined,
        tpLocation: values.locations.length ? values.locations : undefined,
        coSphere: values.spheres.length ? values.spheres : undefined,
        stPnldService: values.hasPndlService || undefined,
        coEducationStage: values.educationStages.length
          ? values.educationStages
          : undefined,
        coYearGrade: values.yearGrades.length ? values.yearGrades : undefined,
      }
    },
    []
  )

  const mapComparativeApiToComponent = useCallback(
    (apiData: DashboardComparativeResponse): ComparativeType | null => {
      if (!apiData) return null
      return {
        prediction: apiData.prediction,
        census: apiData.census,
        stdPercent: apiData.stdPercent,
        classes: apiData.classes,
        schools: apiData.schools,
        region: apiData.region,
        dependency: apiData.dependency,
        location: apiData.location,
        educationStage: apiData.educationStage,
      }
    },
    []
  )

  const { data: predictionYears, isLoading: isPredictionYearsLoading } =
    useDashboardPredictionYears({ onError: errorHandler })

  const { data: censusYears, isLoading: isCensusYearsLoading } =
    useDashboardCensusYears({ onError: errorHandler })

  const { data: comparative, isLoading: isComparativeLoadingRaw } =
    useDashboardComparative(
      filterValues
        ? mapGeoMapFilterToComparativePayload(filterValues)
        : ({} as DashboardComparativePayload),
      { onError: errorHandler, enabled: !!filterValues }
    )

  const isComparativeLoading = isComparativeLoadingRaw && !!filterValues

  const comparativePredictionYearOptions =
    (predictionYears || []).map(year => ({
      label: year.label,
      value: `${year.predictionYear}#${year.censusYear}`,
    })) || []

  const comparativeCensusYearOptions =
    (censusYears || []).map(year => ({
      label: String(year),
      value: String(year),
    })) || []

  const mappedComparative = useMemo(() => {
    return comparative ? mapComparativeApiToComponent(comparative) : null
  }, [comparative, mapComparativeApiToComponent])

  return (
    <Stack gap={4}>
      <GeoMapFilter
        predictionYears={{
          value: comparativePredictionYearOptions,
          isLoading: isPredictionYearsLoading,
          isMulti: false,
        }}
        censusYears={{
          value: comparativeCensusYearOptions,
          isLoading: isCensusYearsLoading,
          isMulti: false,
        }}
        onChangeFilter={values => {
          if (
            (values.predictionYears &&
              ((Array.isArray(values.predictionYears) &&
                values.predictionYears.length > 0) ||
                (typeof values.predictionYears === 'string' &&
                  values.predictionYears.trim() !== ''))) ||
            (values.censusYears &&
              ((Array.isArray(values.censusYears) &&
                values.censusYears.length > 0) ||
                (typeof values.censusYears === 'string' &&
                  values.censusYears.trim() !== '')))
          ) {
            setFilterValues(values as GeoMapFilterValues)
          } else {
            setFilterValues(null)
          }
        }}
      />

      {isComparativeLoading && (
        <Stack alignItems="center" justifyContent="center">
          <Spinner size="xl" />
        </Stack>
      )}

      {!isComparativeLoading && mappedComparative && (
        <ComparativeSingleYear
          comparative={mappedComparative}
          globalFilters={filterValues}
        />
      )}
    </Stack>
  )
}

export default Comparative
